/**
 * Question Classifier Service
 * Analyzes user questions to determine what context is needed for AI responses
 *
 * This enables token-efficient AI interactions by only fetching relevant
 * context based on the question type.
 */

// ===== Question Types =====

export const QUESTION_TYPES = {
    // Funnel-related questions
    FUNNEL_IMPACT: 'funnel_impact',
    FUNNEL_STRATEGY: 'funnel_strategy',
    FUNNEL_BUILD: 'funnel_build',

    // Audience-related questions
    AUDIENCE_SEGMENTATION: 'audience_segmentation',
    AUDIENCE_OVERLAP: 'audience_overlap',
    AUDIENCE_TARGETING: 'audience_targeting',

    // Performance-related questions
    ROAS_IMPROVEMENT: 'roas_improvement',
    CPA_OPTIMIZATION: 'cpa_optimization',
    PERFORMANCE_DIAGNOSIS: 'performance_diagnosis',

    // Budget-related questions
    BUDGET_ALLOCATION: 'budget_allocation',
    BUDGET_OPTIMIZATION: 'budget_optimization',
    SCALING: 'scaling',

    // Campaign action questions
    CAMPAIGN_ACTION: 'campaign_action',
    PAUSE_DECISION: 'pause_decision',
    ENABLE_DECISION: 'enable_decision',

    // Creative-related questions
    CREATIVE_OPTIMIZATION: 'creative_optimization',
    CREATIVE_FATIGUE: 'creative_fatigue',

    // General/catch-all
    GENERAL_ADVICE: 'general_advice',
    SPECIFIC_CAMPAIGN: 'specific_campaign',
    METRICS_EXPLANATION: 'metrics_explanation'
};

// ===== Context Requirements by Question Type =====

/**
 * Maps question types to the knowledge base sections they need
 */
const CONTEXT_REQUIREMENTS = {
    [QUESTION_TYPES.FUNNEL_IMPACT]: {
        required: ['funnel.structure', 'funnel.relationships', 'funnel.analysis'],
        optional: ['performanceByStage', 'budgetAnalysis.distribution'],
        priority: 'high',
        description: 'Questions about the impact of changes on funnel performance'
    },

    [QUESTION_TYPES.FUNNEL_STRATEGY]: {
        required: ['funnel.structure', 'funnel.analysis', 'performanceByStage'],
        optional: ['budgetAnalysis'],
        priority: 'high',
        description: 'Questions about funnel optimization strategy'
    },

    [QUESTION_TYPES.FUNNEL_BUILD]: {
        required: ['funnel.structure', 'funnel.analysis.issues', 'funnel.analysis.opportunities'],
        optional: ['performanceByStage', 'budgetAnalysis'],
        priority: 'medium',
        description: 'Questions about building or improving funnels'
    },

    [QUESTION_TYPES.AUDIENCE_SEGMENTATION]: {
        required: ['funnel.structure', 'performanceByStage'],
        optional: ['campaignIndex'],
        priority: 'high',
        description: 'Questions about audience segmentation strategies'
    },

    [QUESTION_TYPES.AUDIENCE_OVERLAP]: {
        required: ['funnel.structure', 'funnel.relationships'],
        optional: ['campaignIndex'],
        priority: 'medium',
        description: 'Questions about audience overlap between campaigns'
    },

    [QUESTION_TYPES.AUDIENCE_TARGETING]: {
        required: ['funnel.structure', 'performanceByStage'],
        optional: ['budgetAnalysis'],
        priority: 'medium',
        description: 'Questions about audience targeting optimization'
    },

    [QUESTION_TYPES.ROAS_IMPROVEMENT]: {
        required: ['performanceByStage', 'funnel.analysis', 'budgetAnalysis.recommendations'],
        optional: ['trends', 'funnel.structure'],
        priority: 'high',
        description: 'Questions about improving return on ad spend'
    },

    [QUESTION_TYPES.CPA_OPTIMIZATION]: {
        required: ['performanceByStage.conversion', 'budgetAnalysis'],
        optional: ['funnel.structure', 'trends'],
        priority: 'high',
        description: 'Questions about reducing cost per acquisition'
    },

    [QUESTION_TYPES.PERFORMANCE_DIAGNOSIS]: {
        required: ['summary', 'performanceByStage', 'funnel.analysis.issues'],
        optional: ['trends', 'budgetAnalysis'],
        priority: 'high',
        description: 'Questions diagnosing performance issues'
    },

    [QUESTION_TYPES.BUDGET_ALLOCATION]: {
        required: ['budgetAnalysis', 'funnel.analysis.spendDistribution', 'performanceByStage'],
        optional: ['funnel.structure'],
        priority: 'high',
        description: 'Questions about how to allocate budget'
    },

    [QUESTION_TYPES.BUDGET_OPTIMIZATION]: {
        required: ['budgetAnalysis', 'budgetAnalysis.recommendations'],
        optional: ['performanceByStage', 'trends'],
        priority: 'medium',
        description: 'Questions about optimizing budget efficiency'
    },

    [QUESTION_TYPES.SCALING]: {
        required: ['performanceByStage', 'budgetAnalysis', 'funnel.analysis.opportunities'],
        optional: ['funnel.structure', 'trends'],
        priority: 'high',
        description: 'Questions about scaling campaigns or spend'
    },

    [QUESTION_TYPES.CAMPAIGN_ACTION]: {
        required: ['funnel.structure', 'funnel.relationships'],
        optional: ['performanceByStage', 'budgetAnalysis'],
        priority: 'high',
        description: 'Questions about specific campaign actions'
    },

    [QUESTION_TYPES.PAUSE_DECISION]: {
        required: ['funnel.relationships', 'funnel.analysis', 'performanceByStage'],
        optional: ['budgetAnalysis'],
        priority: 'critical',
        description: 'Questions about whether to pause campaigns'
    },

    [QUESTION_TYPES.ENABLE_DECISION]: {
        required: ['funnel.structure', 'budgetAnalysis', 'performanceByStage'],
        optional: ['funnel.analysis'],
        priority: 'medium',
        description: 'Questions about enabling/resuming campaigns'
    },

    [QUESTION_TYPES.CREATIVE_OPTIMIZATION]: {
        required: ['summary', 'performanceByStage'],
        optional: ['trends'],
        priority: 'medium',
        description: 'Questions about creative optimization'
    },

    [QUESTION_TYPES.CREATIVE_FATIGUE]: {
        required: ['trends', 'performanceByStage'],
        optional: ['summary'],
        priority: 'medium',
        description: 'Questions about creative fatigue detection'
    },

    [QUESTION_TYPES.GENERAL_ADVICE]: {
        required: ['summary', 'funnel.analysis.summary'],
        optional: ['performanceByStage', 'budgetAnalysis.recommendations'],
        priority: 'low',
        description: 'General marketing advice questions'
    },

    [QUESTION_TYPES.SPECIFIC_CAMPAIGN]: {
        required: ['campaignIndex'],
        optional: ['funnel.structure', 'performanceByStage'],
        priority: 'medium',
        description: 'Questions about a specific campaign'
    },

    [QUESTION_TYPES.METRICS_EXPLANATION]: {
        required: ['summary'],
        optional: [],
        priority: 'low',
        description: 'Questions asking what metrics mean'
    }
};

// ===== Pattern Matching Rules =====

/**
 * Patterns for classifying questions
 * Each pattern group maps to a question type
 */
const CLASSIFICATION_PATTERNS = [
    // Funnel impact questions
    {
        type: QUESTION_TYPES.FUNNEL_IMPACT,
        patterns: [
            /\b(disable|pause|stop|turn off).*(campaign|campaigns).*(affect|impact|hurt|damage|negative)/i,
            /\b(will|would|does).*(pausing|stopping|disabling).*(affect|impact|hurt)/i,
            /\b(funnel|pipeline).*(break|hurt|damage|impact)/i,
            /\b(affect|impact).*(funnel|pipeline|conversions)/i,
            /\bwhat happens if.*(pause|stop|disable)/i,
            /\b(engagement|awareness|traffic).*(campaigns?).*(affect|impact)/i
        ],
        weight: 10
    },

    // Funnel strategy questions
    {
        type: QUESTION_TYPES.FUNNEL_STRATEGY,
        patterns: [
            /\b(build|create|improve|optimize).*(strong|better|effective).*(funnel)/i,
            /\bhow.*(funnel|pipeline)/i,
            /\b(funnel|pipeline).*(strategy|structure|setup)/i,
            /\b(tofu|mofu|bofu).*(strategy|balance|allocation)/i,
            /\bfunnel.*(health|performance|optimization)/i
        ],
        weight: 8
    },

    // Funnel build questions
    {
        type: QUESTION_TYPES.FUNNEL_BUILD,
        patterns: [
            /\b(build|create|setup|start).*(funnel|pipeline)/i,
            /\b(what|which).*(campaigns?).*(need|should|start)/i,
            /\b(missing|lack|need).*(awareness|consideration|conversion)/i
        ],
        weight: 7
    },

    // Audience segmentation questions
    {
        type: QUESTION_TYPES.AUDIENCE_SEGMENTATION,
        patterns: [
            /\b(segment|split|divide).*(audience|targeting)/i,
            /\baudience.*(segment|segmentation)/i,
            /\bhow.*(segment|split).*(audience|users|customers)/i,
            /\b(different|separate).*(audience|targeting).*(strategy|approach)/i
        ],
        weight: 9
    },

    // Audience overlap questions
    {
        type: QUESTION_TYPES.AUDIENCE_OVERLAP,
        patterns: [
            /\baudience.*(same|overlap|shared|duplicate)/i,
            /\b(same|similar|overlapping).*(audience|targeting)/i,
            /\b(tofu|mofu|bofu).*(audience).*(same|different|overlap)/i,
            /\bare.*(audiences?).*(same|different)/i,
            /\b(compete|competing|cannibalize).*(audience)/i
        ],
        weight: 9
    },

    // Audience targeting questions
    {
        type: QUESTION_TYPES.AUDIENCE_TARGETING,
        patterns: [
            /\b(targeting|target|audience).*(improve|optimize|better)/i,
            /\b(who|which).*(should|target|audience)/i,
            /\b(lookalike|custom|interest).*(audience)/i
        ],
        weight: 6
    },

    // ROAS improvement questions
    {
        type: QUESTION_TYPES.ROAS_IMPROVEMENT,
        patterns: [
            /\b(improve|increase|boost|better).*(roas|return)/i,
            /\broas.*(low|poor|bad|improve|increase)/i,
            /\b(return|roi).*(ad spend|investment|ads)/i,
            /\bhow.*(roas|return)/i,
            /\bget.*(better|higher|more).*(roas|return)/i
        ],
        weight: 9
    },

    // CPA optimization questions
    {
        type: QUESTION_TYPES.CPA_OPTIMIZATION,
        patterns: [
            /\b(reduce|lower|decrease|cut).*(cpa|cost per|acquisition cost)/i,
            /\bcpa.*(high|expensive|too much)/i,
            /\bcost per.*(acquisition|conversion|lead)/i,
            /\b(cheaper|less expensive).*(conversions?|leads?)/i
        ],
        weight: 8
    },

    // Performance diagnosis questions
    {
        type: QUESTION_TYPES.PERFORMANCE_DIAGNOSIS,
        patterns: [
            /\bwhy.*(not|poor|bad|low).*(performing|performance|working)/i,
            /\bwhat.*(wrong|issue|problem)/i,
            /\b(diagnose|analyze|understand).*(performance|issue)/i,
            /\b(performance|results).*(dropped|declining|worse)/i,
            /\bwhat should I focus on/i
        ],
        weight: 8
    },

    // Budget allocation questions
    {
        type: QUESTION_TYPES.BUDGET_ALLOCATION,
        patterns: [
            /\b(allocate|distribute|split).*(budget|spend|money)/i,
            /\bbudget.*(allocation|distribution|split)/i,
            /\bhow much.*(spend|budget|allocate)/i,
            /\b(where|which).*(put|allocate).*(budget|money)/i
        ],
        weight: 8
    },

    // Budget optimization questions
    {
        type: QUESTION_TYPES.BUDGET_OPTIMIZATION,
        patterns: [
            /\b(optimize|maximise|maximize).*(budget|spend)/i,
            /\bbudget.*(efficiency|optimization|waste)/i,
            /\b(wasting|waste).*(budget|money|spend)/i,
            /\bget more.*(budget|spend)/i
        ],
        weight: 7
    },

    // Scaling questions
    {
        type: QUESTION_TYPES.SCALING,
        patterns: [
            /\b(scale|grow|expand|increase).*(campaign|spend|budget|revenue)/i,
            /\bscaling.*(strategy|approach)/i,
            /\b(ready|should).*(scale)/i,
            /\bcan I scale/i,
            /\bhow.*(scale|grow)/i
        ],
        weight: 8
    },

    // Campaign action questions
    {
        type: QUESTION_TYPES.CAMPAIGN_ACTION,
        patterns: [
            /\bshould I.*(pause|stop|disable|enable|resume|start)/i,
            /\b(recommend|suggest).*(pause|stop|start|action)/i,
            /\bwhat.*(do|action).*(campaign)/i
        ],
        weight: 7
    },

    // Pause decision questions
    {
        type: QUESTION_TYPES.PAUSE_DECISION,
        patterns: [
            /\b(pause|stop|disable).*(campaign|campaigns)/i,
            /\bshould.*(pause|stop)/i,
            /\b(turn off|shut down).*(campaign)/i
        ],
        weight: 9
    },

    // Enable decision questions
    {
        type: QUESTION_TYPES.ENABLE_DECISION,
        patterns: [
            /\b(enable|resume|restart|turn on).*(campaign)/i,
            /\bshould.*(enable|resume|restart)/i,
            /\b(start|launch).*(campaign)/i
        ],
        weight: 6
    },

    // Creative optimization questions
    {
        type: QUESTION_TYPES.CREATIVE_OPTIMIZATION,
        patterns: [
            /\b(creative|ad copy|images?|video).*(improve|optimize|better)/i,
            /\b(improve|optimize).*(creative|ad copy|ads)/i,
            /\bwhat.*(creative|ads).*(work|performing)/i
        ],
        weight: 6
    },

    // Creative fatigue questions
    {
        type: QUESTION_TYPES.CREATIVE_FATIGUE,
        patterns: [
            /\bcreative.*(fatigue|tired|stale|worn out)/i,
            /\b(ads?|creatives?).*(fatigue|tired)/i,
            /\bfrequency.*(high|too high)/i
        ],
        weight: 7
    },

    // Metrics explanation questions
    {
        type: QUESTION_TYPES.METRICS_EXPLANATION,
        patterns: [
            /\bwhat (is|does|are).*(roas|cpa|cpc|cpm|ctr|roi)/i,
            /\bexplain.*(metric|roas|cpa)/i,
            /\b(mean|meaning).*(roas|cpa|cpm|ctr)/i
        ],
        weight: 5
    }
];

// ===== Campaign Name Detection =====

/**
 * Extract campaign names or IDs mentioned in the question
 */
export const extractMentionedCampaigns = (question, campaigns) => {
    if (!campaigns || campaigns.length === 0) return [];

    const mentioned = [];
    const lowerQuestion = question.toLowerCase();

    for (const campaign of campaigns) {
        const campaignName = campaign.name?.toLowerCase() || '';

        // Check for exact or partial name matches
        if (campaignName && campaignName.length > 3) {
            // Check if significant portion of campaign name is mentioned
            const words = campaignName.split(/[\s\-_]+/).filter(w => w.length > 2);
            const matchedWords = words.filter(word => lowerQuestion.includes(word));

            if (matchedWords.length >= Math.ceil(words.length / 2)) {
                mentioned.push(campaign.id);
            }
        }

        // Check for ID mention
        if (campaign.id && lowerQuestion.includes(campaign.id.toLowerCase())) {
            mentioned.push(campaign.id);
        }
    }

    return [...new Set(mentioned)]; // Remove duplicates
};

// ===== Main Classification Function =====

/**
 * Classify a user question and determine required context
 */
export const classifyQuestion = (question, campaigns = []) => {
    if (!question || typeof question !== 'string') {
        return {
            type: QUESTION_TYPES.GENERAL_ADVICE,
            confidence: 0,
            requiredContext: CONTEXT_REQUIREMENTS[QUESTION_TYPES.GENERAL_ADVICE].required,
            optionalContext: CONTEXT_REQUIREMENTS[QUESTION_TYPES.GENERAL_ADVICE].optional,
            priority: 'low',
            mentionedCampaigns: []
        };
    }

    const normalizedQuestion = question.trim().toLowerCase();
    const scores = {};

    // Score each pattern group
    for (const rule of CLASSIFICATION_PATTERNS) {
        for (const pattern of rule.patterns) {
            if (pattern.test(normalizedQuestion)) {
                scores[rule.type] = (scores[rule.type] || 0) + rule.weight;
            }
        }
    }

    // Find highest scoring type
    let bestType = QUESTION_TYPES.GENERAL_ADVICE;
    let bestScore = 0;

    for (const [type, score] of Object.entries(scores)) {
        if (score > bestScore) {
            bestScore = score;
            bestType = type;
        }
    }

    // Check for mentioned campaigns
    const mentionedCampaigns = extractMentionedCampaigns(question, campaigns);

    // If specific campaigns are mentioned, add specific campaign context
    if (mentionedCampaigns.length > 0 && bestType === QUESTION_TYPES.GENERAL_ADVICE) {
        bestType = QUESTION_TYPES.SPECIFIC_CAMPAIGN;
    }

    const contextReq = CONTEXT_REQUIREMENTS[bestType];

    return {
        type: bestType,
        confidence: Math.min(bestScore / 10, 1), // Normalize to 0-1
        requiredContext: contextReq.required,
        optionalContext: contextReq.optional,
        priority: contextReq.priority,
        description: contextReq.description,
        mentionedCampaigns
    };
};

/**
 * Get context requirements for a specific question type
 */
export const getContextRequirements = (questionType) => {
    return CONTEXT_REQUIREMENTS[questionType] || CONTEXT_REQUIREMENTS[QUESTION_TYPES.GENERAL_ADVICE];
};

/**
 * Check if a question is high-stakes (affects multiple campaigns or funnel)
 */
export const isHighStakesQuestion = (classification) => {
    const highStakesTypes = [
        QUESTION_TYPES.FUNNEL_IMPACT,
        QUESTION_TYPES.PAUSE_DECISION,
        QUESTION_TYPES.CAMPAIGN_ACTION,
        QUESTION_TYPES.SCALING
    ];

    return highStakesTypes.includes(classification.type) ||
        classification.priority === 'critical' ||
        classification.priority === 'high';
};

/**
 * Get suggested follow-up questions based on question type
 */
export const getSuggestedFollowUps = (questionType) => {
    const followUps = {
        [QUESTION_TYPES.FUNNEL_IMPACT]: [
            'Which campaigns should I prioritize in my funnel?',
            'How can I minimize the impact on my conversions?',
            'What audiences will be affected?'
        ],
        [QUESTION_TYPES.FUNNEL_STRATEGY]: [
            'How should I allocate budget across funnel stages?',
            'What is missing from my current funnel?',
            'How can I improve audience flow between stages?'
        ],
        [QUESTION_TYPES.ROAS_IMPROVEMENT]: [
            'Which campaigns are dragging down my ROAS?',
            'Should I reallocate budget from underperformers?',
            'How can I improve my conversion campaigns?'
        ],
        [QUESTION_TYPES.AUDIENCE_SEGMENTATION]: [
            'Are my audiences overlapping too much?',
            'How should I structure lookalike audiences?',
            'Should I separate TOFU and MOFU audiences?'
        ],
        [QUESTION_TYPES.BUDGET_ALLOCATION]: [
            'Am I spending too much on awareness vs conversions?',
            'Which campaigns deserve more budget?',
            'How should I redistribute from underperformers?'
        ],
        [QUESTION_TYPES.SCALING]: [
            'What is the safest way to scale without losing efficiency?',
            'Which campaigns should I scale first?',
            'How much can I increase budgets before performance drops?'
        ]
    };

    return followUps[questionType] || [
        'What should I focus on to improve performance?',
        'Are there any quick wins I should implement?',
        'What is the biggest opportunity right now?'
    ];
};

export default {
    QUESTION_TYPES,
    classifyQuestion,
    getContextRequirements,
    extractMentionedCampaigns,
    isHighStakesQuestion,
    getSuggestedFollowUps
};
